<script setup lang="ts">
  import RecipientCard from '@/components/drawer/DrawerRecipients/RecipientCard.vue'
  import { useAppStore } from '@/store/app'
  import { useBuilderStore } from '@/store/builder'
  import {
    ELEMENTS_META,
    ELEMENTS_STYLES,
    getMarginProperties,
    getPaddingProperties,
    IElement,
    IRecipient,
    onChangeMarginPadding,
    renderLabelAvatarOnSelect,
    SignatureElementOptions,
    UIBuilderSpacing,
  } from '@gohighlevel/ghl-proposals-common'
  import { UICheckbox, UIFormItem, UIInput, UIRadioGroup, UIRadioGroupItem, UISelect, UISpace, UIContentWrap } from '@gohighlevel/ghl-ui'
  import { computed, onMounted, PropType, ref, toRaw, watch } from 'vue'
  import { useElementOption } from '../../../composable/elementOptions.js'
  import { useRecipients } from '../../../composition/useRecipients.js'
  import { useI18n } from 'vue-i18n'
  const i18nLocale = useI18n()
  const { t } = i18nLocale
  
  const props = defineProps({
    element: {
      type: Object as PropType<IElement<SignatureElementOptions>>,
      required: true,
    },
  })

  const { updateOption, updateStyles } = useElementOption()
  const { syncRecipient } = useBuilderStore()
  const { signedByOptionsWithStaff, assignRecipientToFillableElement } = useRecipients()

  const recipients = computed(() => appStore.document?.recipients)
  const appStore = useAppStore()
  const recipient = ref()
  const selectValue = ref()
  const searchQuery = ref('')
  const margin = ref(getMarginProperties(props.element))
  const padding = ref(getPaddingProperties(props.element))

  onMounted(() => {
    syncRecipientList()
  })

  const syncRecipientList = () => {
    syncRecipient()
    fetchRecipientData()
    getSelectValue()
  }

  const getSelectValue = () => {
    const recipientsList = toRaw(recipients.value)
    const lastSelectedId = props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof SignatureElementOptions]

    const userStillExistsInRecipients = recipientsList.find((el: any) => {
      return el.id === lastSelectedId
    })

    if (lastSelectedId && !userStillExistsInRecipients) {
      selectValue.value = null
      return
    }

    if (
      props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof SignatureElementOptions] &&
      props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof SignatureElementOptions]
    ) {
      selectValue.value = `${props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof SignatureElementOptions]}_${
        props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof SignatureElementOptions]
      }`
    }
  }

  const fetchRecipientData = (recipientId?: string, entityName?: string) => {
    const assignedRecipient = recipients.value.find((elem: IRecipient) => {
      if (recipientId && entityName) {
        return elem.id === recipientId && elem.entityName === entityName
      } else {
        return (
          elem.id === props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof SignatureElementOptions] &&
          elem.entityName === props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof SignatureElementOptions]
        )
      }
    })
    if (assignedRecipient) {
      recipient.value = assignedRecipient
    }
  }

  const assignRecipientToSignature = (val: string) => {
    const recipientData = val.split('_')
    if (recipientData.length !== 2 || !props.element) {
      return
    }

    const recipientId = recipientData[0]
    const entityName = recipientData[1]

    // Use the shared function to assign recipient
    assignRecipientToFillableElement(val, props.element)

    // Fetch recipient data and sync
    fetchRecipientData(recipientId, entityName)
    syncRecipientList()
  }

  watch(props, newValue => {
    if (newValue) {
      syncRecipientList()
    }
    if (props.element) {
      margin.value = getMarginProperties(props.element)
      padding.value = getPaddingProperties(props.element)
    }
  })
  const onBoxStyleChange = onChangeMarginPadding(updateStyles)

  const filteredSignedByOptions = computed(() => {
    if (!searchQuery.value || searchQuery.value.trim() === '') {
      return signedByOptionsWithStaff.value
    }
    const query = searchQuery.value.toLowerCase().trim()
    return signedByOptionsWithStaff.value.filter((option: any) => {
      const label = option.label?.toLowerCase() || ''
      return label.includes(query)
    })
  })

  const handleSearch = (query: string) => {
    searchQuery.value = query
  }
</script>

<template>
  <div class="hl-proposal-sidebar-content">
    <div class="mb-4">
      <UIFormItem :label="$t('common.toBeSignedBy')">
        <UISelect
          :id="`signature-text`"
          :placeholder="$t('common.assignRecipientToSignature')"
          :options="filteredSignedByOptions"
          :value="selectValue"
          filterable
          @update:value="(val: any) => assignRecipientToSignature(val)"
          @search="handleSearch"
          :key="`signature-${selectValue}`"
          :render-label="renderLabelAvatarOnSelect"
        />
      </UIFormItem>
      <div class="border-b">
        <RecipientCard :recipient="recipient" :key="`signature-card-${selectValue}`" />
      </div>
    </div>
    <UIContentWrap
      :locale="i18nLocale?.locale?.value || 'en-US'"
      class="p-0"
      style="display: contents"
    >
      <UIFormItem :label="$t('common.placeholder')">
        <UIInput
          id="signature-text"
          :model-value="element?.component.options.text"
          @update:model-value="val => updateOption(ELEMENTS_META.TEXT, val)"
        />
      </UIFormItem>
    </UIContentWrap>

    <UIFormItem :label="$t('common.align')" v-if="!props.element?.component.isDraggable">
      <UIRadioGroup :value="element?.responsiveStyles.large.align" @update:value="val => updateStyles(ELEMENTS_STYLES.ALIGN, val)">
        <UISpace>
          <UIRadioGroupItem value="signature-left">
            <template #content>{{ $t('common.left') }}</template>
          </UIRadioGroupItem>
          <UIRadioGroupItem value="signature-center">
            <template #content>{{ $t('common.center') }}</template>
          </UIRadioGroupItem>
          <UIRadioGroupItem value="signature-right">
            <template #content>{{ $t('common.right') }}</template>
          </UIRadioGroupItem>
        </UISpace>
      </UIRadioGroup>
    </UIFormItem>
    <UICheckbox
      v-if="props.element?.version > 1"
      :id="`${element?.id}-show_name`"
      :disabled="false"
      :checked="element?.component.options.showName"
      @update:checked="
        (val: boolean) => {
          updateOption(ELEMENTS_META.SHOW_NAME, val, element?.id)
        }
      "
    >
      {{ $t('common.showSignerName') }}
    </UICheckbox>
    <UIBuilderSpacing v-if="!props.element?.component.isDraggable" :element="props.element" @onUpdate="onBoxStyleChange" />
  </div>
</template>

<style lang="scss">
  .n-avatar__text {
    transform: translateX(-50%) translateY(-50%) scale(1);
  }
</style>
